import React, { useState, useEffect } from 'react';
import api from '../../api';
import DiglettDashGame from '../../Pages/MiniGames/DiglettDash/DiglettDashGame';
import firstPlaceImg from "../../assets/images/items/1st.png";
import secondPlaceImg from "../../assets/images/items/2nd.png";
import thirdPlaceImg from "../../assets/images/items/3rd.png";
const DiglettDashModal = ({ isOpen, toggle, playerPos, spawnText }) => {
  const [gameState, setGameState] = useState('welcome'); // 'welcome', 'playing', 'results'
  const [gameStats, setGameStats] = useState({
    playsToday: 0,
    maxPlays: 5,
    tickets: 0
  });
  const [showResults, setShowResults] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ENTRY_FEE = 20;
  const TICKET_REWARDS = { 1: 100, 2: 50, 3: 25, 4: 0 };

  useEffect(() => {
    if (isOpen) {
      loadGameStats();
      setGameState('welcome');
      setError(null);
    } else {
      resetGame();
    }
  }, [isOpen]);

  // Reset game state completely
  const resetGame = () => {
    setGameState('welcome');
    setShowResults(false);
    setError(null);
  };

  // Load player's game stats
  const loadGameStats = async () => {
    try {
      setLoading(true);
      const response = await api.get("/v1/diglett-dash/stats", { withCredentials: true });
      setGameStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Error loading diglett dash stats:", err);
      setError("Failed to load game stats. Please try again.");
      setLoading(false);
    }
  };

  // Start a new game
  const handleStartGame = async () => {
    try {
      // Check if player has enough coins and hasn't reached daily limit
      const response = await api.post("/v1/diglett-dash/start", { 
        cost: ENTRY_FEE 
      }, { withCredentials: true });
      
      if (response.data.success) {
        setGameState('playing');
        setShowResults(false);
        
        if (spawnText && playerPos) {
          spawnText("Starting race!", playerPos.x, playerPos.y - 20, "#4CAF50");
        }
      } else {
        setError(response.data.message || "Failed to start game");
      }
    } catch (err) {
      console.error("Error starting diglett dash game:", err);
      const errorMessage = err.response?.data?.detail || "Failed to start game. Please try again.";
      setError(errorMessage);
      if (spawnText && playerPos) {
        spawnText(errorMessage, playerPos.x, playerPos.y - 20, "#f44336");
      }
    }
  };

  // End the current game
  const handleGameComplete = async (playerWon, finalPosition) => {
    setGameState('welcome');
    
    try {
      // Calculate tickets earned based on position
      const ticketsEarned = TICKET_REWARDS[finalPosition] || 0;
      
      // Submit result to backend
      const endResponse = await api.post("/v1/diglett-dash/end", {
        final_position: finalPosition,
        tickets_earned: ticketsEarned
      }, { withCredentials: true });
      
      if (endResponse.data.success) {
        // Fetch updated stats from server
        const statsResponse = await api.get("/v1/diglett-dash/stats", { withCredentials: true });
        const newStats = statsResponse.data;

        // Set the data for the results screen
        setResultsData({
          finalPosition: finalPosition,
          ticketsEarned: ticketsEarned,
          totalTickets: newStats.tickets,
          playsToday: newStats.playsToday,
          maxPlays: newStats.maxPlays
        });

        // Update the main gameStats for the pre-game screen
        setGameStats(newStats);

        // Show the results screen
        setShowResults(true);
        
        // Show floating text with tickets earned
        if (spawnText && playerPos) {
          const message = `You finished ${finalPosition}${getOrdinalSuffix(finalPosition)} and earned ${ticketsEarned} tickets!`;
          spawnText(message, playerPos.x, playerPos.y - 20, ticketsEarned > 0 ? "#4CAF50" : "#ff9800");
        }
      } else {
        setError(endResponse.data.message || "Failed to submit result");
      }
    } catch (err) {
      console.error("Error ending diglett dash game:", err);
      setError("Failed to submit result. Please try again.");
    }
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  };

  const handleClose = () => {
    toggle();
    setGameState('welcome');
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div 
        className="game-popup-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}
      >
        <div style={{
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          padding: '20px',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          textAlign: 'center'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className="game-popup-overlay"
        onClick={toggle}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}
      >
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
            color: '#fff',
            padding: '20px',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}
        >
          <h3 style={{ margin: '0 0 15px 0' }}>Error: {error}</h3>
          <button 
            onClick={toggle}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="game-popup-overlay"
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="diglett-dash-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'linear-gradient(to bottom, rgba(29, 34, 46, 0.95), rgba(41, 50, 65, 0.95))',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)',
          flexWrap: 'wrap',
          gap: '8px',
          minHeight: '60px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: '1 1 auto',
            minWidth: '200px'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>🏃‍♂️ Diglett Dash</h3>
          </div>

          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#fff',
              borderRadius: '4px',
              padding: '6px 12px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '14px'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '16px',
          flex: 1,
          overflow: 'auto'
        }}>
          {gameState === 'welcome' && !showResults && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.05)', 
                padding: '16px', 
                borderRadius: '8px', 
                marginBottom: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h2>Welcome to Diglett Dash!</h2>
                <p>Race your Diglett against 3 NPCs up the tunnel!</p>
                <p>• Use ⬆️ Arrow Key (PC) or tap the button (Mobile)</p>
                <p>• First to reach the top wins!</p>
                <p>
                  Cost to play: <img src="items/coin.png" alt="Coin" style={{ height: '20px', verticalAlign: 'middle' }} /> {ENTRY_FEE}
                </p>
                <div className="game-stats-summary">
                  <p>You have played {gameStats.playsToday} of {gameStats.maxPlays} races today</p>
                  <p>Total tickets earned: <img src="items/Ticket.png" alt="Ticket" style={{ height: '20px', verticalAlign: 'middle', marginRight: '5px' }} /> {gameStats.tickets}</p>
                  <div style={{ marginTop: '10px' }}>
                    <strong>Ticket Rewards:</strong>
                    <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '5px' }}>
                      <span><img src="items/1st.png" alt="1st" /> {TICKET_REWARDS[1]} tickets</span>
                      <span><img src="items/2nd.png" alt="1st" />  {TICKET_REWARDS[2]} tickets</span>
                      <span><img src="items/3rd.png" alt="1st" /> {TICKET_REWARDS[3]} tickets</span>
                    </div>
                  </div>
                </div>
                
                {gameStats.playsToday < gameStats.maxPlays ? (
                  <button 
                    onClick={handleStartGame}
                    style={{
                      background: 'rgba(76, 175, 80, 0.8)',
                      color: '#fff',
                      border: 'none',
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      marginTop: '15px'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(76, 175, 80, 1)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(76, 175, 80, 0.8)';
                    }}
                  >
                    Start Race! (Cost: <img src="items/coin.png" alt="Coin" style={{ height: '16px', verticalAlign: 'middle' }} />{ENTRY_FEE})
                  </button>
                ) : (
                  <div style={{ 
                    background: 'rgba(255, 193, 7, 0.2)', 
                    color: '#ffc107', 
                    padding: '10px', 
                    borderRadius: '8px', 
                    marginTop: '15px',
                    border: '1px solid rgba(255, 193, 7, 0.3)'
                  }}>
                    You've reached your daily race limit. Come back tomorrow!
                  </div>
                )}
              </div>
            </div>
          )}
          
          {showResults && resultsData && (
            <div style={{ textAlign: 'center' }}>
              <h2>Race Complete!</h2>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '18px', marginBottom: '10px' }}>
                  You finished in {resultsData.finalPosition}{getOrdinalSuffix(resultsData.finalPosition)} place!
                </p>
                <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                  You earned: {resultsData.ticketsEarned} <img src="items/Ticket.png" alt="Ticket" style={{ height: '24px', marginLeft: '5px' }} />
                </p>
                <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', opacity: 0.8 }}>
                  Total tickets: {resultsData.totalTickets} <img src="items/Ticket.png" alt="Ticket" style={{ height: '20px', marginLeft: '5px' }} />
                </p>
              </div>
              
              {resultsData.playsToday < resultsData.maxPlays ? (
                <button 
                  onClick={handleStartGame}
                  style={{
                    background: 'rgba(76, 175, 80, 0.8)',
                    color: '#fff',
                    border: 'none',
                    padding: '12px 24px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(76, 175, 80, 1)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(76, 175, 80, 0.8)';
                  }}
                >
                  Race Again! (Cost: <img src="items/coin.png" alt="Coin" style={{ height: '16px', verticalAlign: 'middle' }} />{ENTRY_FEE})
                </button>
              ) : (
                <div style={{ 
                  background: 'rgba(255, 193, 7, 0.2)', 
                  color: '#ffc107', 
                  padding: '10px', 
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 193, 7, 0.3)'
                }}>
                  You've reached your daily race limit. Come back tomorrow!
                </div>
              )}
            </div>
          )}
          
          {gameState === 'playing' && (
            <DiglettDashGame 
              onClose={handleClose}
              onGameComplete={handleGameComplete}
              entryFee={ENTRY_FEE}
              isModal={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DiglettDashModal;